import { TrashIcon } from '@heroicons/react/24/solid';
import { RowActionsMenu } from '@src/components/DataTableActions';
import { Typography } from 'antd';
import { FaCircleExclamation } from 'react-icons/fa6';
import { IoLockClosed } from 'react-icons/io5';
import { NavigateFunction } from 'react-router-dom';
import { ColumnHeaderWithMenu } from '@src/components/Table/components/ColumnHeaderWithMenu';
import { SortDirection } from '@src/types/common';
import {
    ACTION_ALLOWED_ROLES,
    MODULE_KEYS,
} from '@src/constants/permissions.const';
import { usePermissions } from '@src/hooks';
import { ItemsPreview } from '@src/components/ItemsPreview/ItemsPreview';
import { ShowMoreText } from '@src/components/ui/ShowMoreText';
import { ActionGuard } from '@src/components/permissions';
import { ManageColumnsDropdown } from '@src/components/Table/components';
import { TFunction } from 'i18next';
import { LockClosedIcon } from '@heroicons/react/24/outline';

export type actionType = 'delete' | 'details';

interface RenderColumnsProps {
    t: TFunction;
    sorterState?: {
        sortBy: string | null;
        sortOrder: SortDirection | null;
        onSortChange: (columnKey: string, order: SortDirection | null) => void;
    };
    hiddenColumns: string[];
    setHiddenColumns: (keys: string[]) => void;
    onActionClick: (action: actionType, row: any) => void;
}

export function RenderColumns({
    t,
    sorterState,
    hiddenColumns,
    setHiddenColumns,
    onActionClick,
}: RenderColumnsProps) {
    const { permissionOf } = usePermissions();
    const baseColumns = [
        {
            key: 'name',
            title: t('pages.role.dataTable.name'),
            sortable: true,
            render: (item) => (
                <div className="flex items-center gap-1">
                    {item.isGlobalDefault && (
                        <IoLockClosed
                            className="text-[#4C4C4C7A] text-opacity-[48%]"
                            size={20}
                        />
                    )}
                    <Typography className="font-[500]  text-[#233AC6]">
                        {item.name}
                    </Typography>
                    <p className="italic font-[400] text-[#4C4C4C7A] text-opacity-[48%]">
                        {item.isGlobalDefault && '(Default)'}
                    </p>
                </div>
            ),
        },
        {
            key: 'userCount',
            dataIndex: 'userCount',
            title: t('pages.role.dataTable.assignedUser'),
            sortable: true,
        },
        {
            key: 'eligibleAccountNames',
            dataIndex: 'eligibleAccountNames',
            title: t('pages.role.dataTable.account'),
            sortable: true,
            render: (_: any, rowData: any) => (
                <ItemsPreview
                    items={rowData.eligibleAccountNames}
                    maxItems={2}
                />
            ),
        },
        {
            key: 'updatedAt',
            title: t('pages.role.dataTable.updatedAt'),
            sortable: true,
            render: (row) =>
                row.updatedAt
                    ? new Date(row.updatedAt)
                          .toLocaleString()
                          .replace(',', ' -')
                    : '',
        },
        {
            key: 'description',
            dataIndex: 'description',
            title: t('pages.role.dataTable.description'),
            sortable: true,
            render: (_: any, rowData: any) => {
                return rowData.description ? (
                    <ShowMoreText
                        text={rowData.description}
                        title={t('pages.role.dataTable.description')}
                        previewLength={30}
                    />
                ) : null;
            },
        },
    ];

    const canSeeActions =
        (permissionOf(MODULE_KEYS.RoleManagement) &&
            ACTION_ALLOWED_ROLES.includes(
                permissionOf(MODULE_KEYS.RoleManagement)!
            )) ||
        hiddenColumns.length > 0;

    const actionsColumn = canSeeActions
        ? {
              key: 'actions',
              dataIndex: 'actions',
              className: 'actions-cell',
              title: (
                  <ManageColumnsDropdown
                      t={t}
                      allColumns={baseColumns}
                      hiddenColumns={hiddenColumns}
                      setHiddenColumns={setHiddenColumns}
                  />
              ),
              fixed: 'right',
              render: (_: any, row: any) => {
                  if (row.isMainRole) {
                      return (
                          <div
                              className="flex justify-center items-center text-gray-400"
                              aria-label="Main role - actions locked"
                              title="Main role - actions locked"
                          >
                              <LockClosedIcon width={16} height={16} />
                          </div>
                      );
                  }

                  const actions = [
                      {
                          key: 'details',
                          label: t('pages.role.dataTable.details'),
                          icon: (
                              <FaCircleExclamation
                                  size={16}
                                  className="w-4 h-4 mr-3"
                              />
                          ),
                          onClick: () => onActionClick('details', row),
                      },
                      {
                          key: 'delete',
                          label: t('pages.role.dataTable.delete'),
                          icon: <TrashIcon width={16} height={16} />,
                          onClick: () => onActionClick('delete', row),
                      },
                  ];

                  return (
                      <ActionGuard module="RoleManagement">
                          <div
                              onClick={(e) => {
                                  e.stopPropagation();
                              }}
                          >
                              <RowActionsMenu dataRow={row} actions={actions} />
                          </div>
                      </ActionGuard>
                  );
              },
              width: 1,
          }
        : null;

    const enhancedColumns = [
        ...baseColumns.map((col) => ({
            ...col,
            title: (
                <ColumnHeaderWithMenu
                    title={col.title}
                    columnKey={col.key}
                    allColumns={baseColumns}
                    hiddenColumns={hiddenColumns}
                    setHiddenColumns={setHiddenColumns}
                    sortable={!!col.sortable}
                    sortOrder={
                        sorterState?.sortBy === col.key
                            ? sorterState.sortOrder
                            : null
                    }
                    onSortChange={(order: SortDirection | null) =>
                        sorterState?.onSortChange(col.key, order)
                    }
                    onColumnMenuClick={(colKey, action) => {
                        switch (action) {
                            case 'hide':
                                if (!hiddenColumns.includes(colKey))
                                    setHiddenColumns([
                                        ...hiddenColumns,
                                        colKey,
                                    ]);
                                break;
                            case 'sortAsc':
                                sorterState?.onSortChange(colKey, 'ASC');
                                break;
                            case 'sortDesc':
                                sorterState?.onSortChange(colKey, 'DESC');
                                break;
                        }
                    }}
                />
            ),
        })),
        actionsColumn,
    ];

    return enhancedColumns
        .filter((col) => col !== null)
        .filter((col) => !hiddenColumns.includes(col.key)) as any;
}
